<template>
  <section class="wv-step" :class="{ 'is-active': active }" :id="step.id" :data-step="Number(step.number)">
    <div class="wv-step-head">
      <span class="wv-step-num">{{ step.number }}</span>
      <h2>{{ step.title }}</h2>
    </div>
    <span class="wv-who" :class="`wv-role-${step.role}`"><i />{{ step.audience }}</span>
    <p v-for="(paragraph, index) in paragraphs" :key="index">
      <template v-for="(part, partIndex) in paragraph"
        ><b v-if="part.bold" :key="partIndex">{{ part.text }}</b
        ><span v-else :key="partIndex">{{ part.text }}</span></template
      >
    </p>
    <div v-if="step.chips?.length" class="wv-docs">
      <span v-for="(chip, index) in step.chips" :key="index" class="wv-doc">
        <template v-if="chip.includes(' · ')"
          ><b>{{ chip.split(' · ')[0] }}</b> · {{ chip.split(' · ').slice(1).join(' · ') }}</template
        >
        <template v-else>{{ chip }}</template>
      </span>
    </div>
  </section>
</template>

<script>
import { splitEmphasis } from './utils/workflow-copy'

export default {
  name: 'WorkflowStoryStep',
  props: {
    step: { type: Object, required: true },
    active: { type: Boolean, default: false },
    emphasisTerms: { type: Array, default: () => [] }
  },
  computed: {
    paragraphs() {
      return String(this.step.copy || '')
        .split(/\n\s*\n/)
        .filter(Boolean)
        .map(copy => splitEmphasis(copy, this.emphasisTerms))
    }
  }
}
</script>

<style scoped>
.wv-step {
  padding: 38px 0;
  border-bottom: 1px solid var(--border-color-split);
  opacity: 0.3;
  transition: opacity 0.45s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.wv-step.is-active {
  opacity: 1;
}
.wv-step:last-of-type {
  border-bottom: none;
}

.wv-step-head {
  display: flex;
  align-items: baseline;
  gap: 18px;
  margin-bottom: 12px;
}

.wv-step-num {
  flex-shrink: 0;
  width: 28px;
  padding-top: 6px;
  color: var(--text-color-alpha4);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
}

.wv-step.is-active .wv-step-num {
  color: var(--text-color-alpha6);
}

.wv-step h2 {
  margin: 0;
  color: var(--text-color);
  font-size: 28px;
  font-weight: 700;
  line-height: 1.14;
}

.wv-step p {
  max-width: 66ch;
  margin: 0 0 0 46px;
  color: var(--text-color-alpha6);
  font-size: 15px;
  line-height: 1.72;
  white-space: pre-line;
}

.wv-step p + p {
  margin-top: 16px;
}

.wv-who {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 13px 46px;
  padding: 4px 9px 4px 7px;
  border: 1px solid currentColor;
  border-radius: 3px;
  font-size: 9.5px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.wv-who i {
  display: block;
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}

.wv-role-all {
  color: var(--wv-all);
}
.wv-role-employee {
  color: var(--wv-employee);
}
.wv-role-contractor {
  color: var(--wv-contractor);
}

.wv-who.wv-role-all {
  background: rgba(var(--wv-all-rgb), 0.08);
}
.wv-who.wv-role-employee {
  background: rgba(var(--wv-employee-rgb), 0.07);
}
.wv-who.wv-role-contractor {
  background: rgba(var(--wv-contractor-rgb), 0.07);
}

.wv-docs {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 16px 0 0 46px;
}

.wv-doc {
  max-width: 290px;
  padding: 5px 10px;
  border: 1px solid var(--border-color-base);
  border-radius: 3px;
  background: var(--bg-card);
  color: var(--text-color-alpha6);
  font-size: 10.5px;
  white-space: normal;
}

.wv-doc b {
  color: var(--text-color);
  font-weight: 500;
}

@media (max-width: 1180px) {
  .wv-step h2 {
    font-size: 24px;
  }
  .wv-step p,
  .wv-who,
  .wv-docs {
    margin-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wv-step {
    transition: none;
  }
}
</style>
